import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const IDLE_THRESHOLD_MS = 30 * 60 * 1000;
const RELOAD_THRESHOLD_MS = 6 * 60 * 60 * 1000; // 6 hours — likely stale chunks after deployment

export const useIdleRecovery = (): void => {
  const queryClient = useQueryClient();
  const lastActiveRef = useRef<number>(Date.now());

  useEffect(() => {
    const update = () => {
      lastActiveRef.current = Date.now();
    };
    window.addEventListener('pointerdown', update);
    window.addEventListener('keydown', update);
    return () => {
      window.removeEventListener('pointerdown', update);
      window.removeEventListener('keydown', update);
    };
  }, []);

  // On tab resume after long idle: invalidate queries, or hard reload if very stale
  // (hard reload picks up freshly deployed JS/CSS chunks and avoids dynamic-import errors).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) return;
      const idle = Date.now() - lastActiveRef.current;
      lastActiveRef.current = Date.now();
      if (idle > RELOAD_THRESHOLD_MS) {
        window.location.reload();
        return;
      }
      if (idle > IDLE_THRESHOLD_MS) {
        void queryClient.invalidateQueries({ queryKey: ['th'] });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [queryClient]);
};
